/* src/pages/admin/AdminBlog.jsx */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Trash2,
  PencilLine,
  PlusCircle,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Search as SearchIcon,
  BookOpenText,
} from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-hot-toast";

const API = process.env.REACT_APP_SERVER_URL;
const PER_PAGE = 10;

const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    title: "",
    description: "",
    author: "",
    date: new Date().toISOString().slice(0, 10),
    content: "",
  });
  const [editId, setEditId] = useState(null);
  const [activePost, setActivePost] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const fetchBlogs = async () => {
    try {
      const { data } = await axios.get(`${API}/api/blogs`);
      setBlogs(data);
    } catch {
      toast.error("Couldn't load posts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return blogs;
    return blogs.filter(
      (b) =>
        b.title.toLowerCase().includes(query.toLowerCase()) ||
        b.description.toLowerCase().includes(query.toLowerCase()),
    );
  }, [blogs, query]);

  const maxPage = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      author: "",
      date: new Date().toISOString().slice(0, 10),
      content: "",
    });
    setEditId(null);
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("description", form.description);
    formData.append("author", form.author);
    formData.append("date", form.date);
    formData.append("content", form.content);
    if (imageFile) formData.append("image", imageFile);

    try {
      if (editId) {
        await axios.put(`${API}/api/blogs/${editId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/api/blogs`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Post created");
      }
      resetForm();
      fetchBlogs();
    } catch {
      toast.error("Couldn't save post. Please try again.");
    }
  };

  const handleEdit = (p) => {
    setForm({
      title: p.title,
      description: p.description,
      author: p.author,
      date: p.date?.slice(0, 10),
      content: p.content,
      image: p.image || "",
    });
    setImageFile(null);
    setEditId(p._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await axios.delete(`${API}/api/blogs/${id}`);
      setBlogs((prev) => prev.filter((b) => b._id !== id));
      toast.success("Post deleted");
    } catch {
      toast.error("Couldn't delete post. Please try again.");
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Page Header + Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Blog Manager
          </h1>
          <p className="text-gray-500 mt-1">Create and manage blog posts</p>
        </div>
        <label className="relative w-full md:w-80">
          <SearchIcon
            className="absolute left-3.5 top-3 text-gray-400"
            size={18}
          />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search posts..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition shadow-sm"
          />
        </label>
      </div>

      {/* Form */}
      <div className="bg-white border border-gray-100 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <span className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-white text-sm">
            {editId ? "✏" : "+"}
          </span>
          {editId ? "Edit Post" : "New Post"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Title</label>
            <input
              name="title"
              placeholder="Post title"
              value={form.title}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Short Description
            </label>
            <input
              name="description"
              placeholder="Brief description"
              value={form.description}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700">
                Author
              </label>
              <input
                name="author"
                placeholder="Author name"
                value={form.author}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700">
                Date
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Cover Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
            />
            {(imageFile || form.image) && (
              <img
                src={imageFile ? URL.createObjectURL(imageFile) : form.image}
                alt="Preview"
                className="w-24 h-24 mt-2 rounded-2xl object-cover border-2 border-yellow-100 shadow"
              />
            )}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Full Content
            </label>
            <textarea
              name="content"
              placeholder="Write your blog content here..."
              value={form.content}
              onChange={handleChange}
              rows={7}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition resize-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold hover:from-yellow-600 hover:to-yellow-700 shadow-lg shadow-yellow-500/20 transition flex items-center gap-2"
            >
              <PlusCircle size={16} />
              {editId ? "Update Post" : "Create Post"}
            </button>
            {editId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Blog List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <CircularProgress size={35} sx={{ color: "#eab308" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-[2rem] flex flex-col items-center justify-center h-64 gap-3">
          <BookOpenText className="w-12 h-12 text-gray-200" />
          <p className="text-gray-400 font-bold text-sm tracking-tight">
            No articles discovered
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginated.map((post) => (
              <div
                key={post._id}
                className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-300 flex gap-4 group"
              >
                {post.image && (
                  <div className="relative w-20 h-20 flex-shrink-0">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover rounded-xl border border-gray-50"
                    />
                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
                  </div>
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                  <div>
                    <h3 className="font-black text-gray-900 text-sm mb-1 truncate group-hover:text-yellow-600 transition-colors uppercase tracking-tight">
                      {post.title}
                    </h3>
                    <p className="text-[11px] text-gray-400 font-medium line-clamp-2 leading-tight italic">
                      "{post.description}"
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">
                      By{" "}
                      <span className="text-yellow-600 font-black">
                        {post.author?.split(" ")[0]}
                      </span>{" "}
                      ·{" "}
                      {new Date(post.date).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setActivePost(post)}
                        className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-all"
                        title="Quick Preview"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleEdit(post)}
                        className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
                        title="Edit Entry"
                      >
                        <PencilLine size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(post._id)}
                        className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                        title="Delete Entry"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {maxPage > 1 && (
            <div className="flex justify-center items-center gap-3 pb-4">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-semibold text-gray-700 bg-white border border-gray-100 px-4 py-2 rounded-xl">
                {page} / {maxPage}
              </span>
              <button
                disabled={page === maxPage}
                onClick={() => setPage((p) => p + 1)}
                className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      {activePost && (
        <div className="fixed inset-0 bg-gray-950/40 backdrop-blur-md flex items-center justify-center z-[100] p-4 sm:p-6 transition-all duration-500">
          <div className="bg-white/90 backdrop-blur-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] border border-white/20 relative flex flex-col animate-in fade-in zoom-in-95 duration-500">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-2xl flex items-center justify-center shadow-lg shadow-yellow-500/20">
                  <BookOpenText className="text-white w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">
                    Article Preview
                  </h2>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none mt-0.5">
                    Draft Review Mode
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActivePost(null)}
                className="w-10 h-10 bg-gray-50 hover:bg-red-50 hover:text-red-500 rounded-2xl flex items-center justify-center transition-all active:scale-95"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-8 sm:p-12">
              <div className="max-w-2xl mx-auto space-y-8">
                {activePost.image && (
                  <img
                    src={activePost.image}
                    alt={activePost.title}
                    className="w-full h-80 object-cover rounded-[2rem] shadow-2xl border-4 border-white shadow-yellow-500/5"
                  />
                )}

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-[10px] font-black text-yellow-600 bg-yellow-50 w-fit px-3 py-1.5 rounded-full uppercase tracking-widest">
                    <span>{activePost.author}</span>
                    <span className="w-1 h-1 bg-yellow-300 rounded-full" />
                    <span>
                      {new Date(activePost.date).toLocaleDateString(undefined, {
                        dateStyle: "long",
                      })}
                    </span>
                  </div>
                  <h1 className="text-4xl font-black text-gray-900 leading-[1.1] tracking-tight">
                    {activePost.title}
                  </h1>
                  <p className="text-xl text-gray-400 font-medium italic leading-relaxed">
                    "{activePost.description}"
                  </p>
                </div>

                <div
                  className="prose prose-yellow max-w-none text-gray-700 leading-relaxed font-medium text-base pt-8 border-t border-gray-100"
                  dangerouslySetInnerHTML={{ __html: activePost.content }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setActivePost(null)}
                className="px-8 py-3 rounded-2xl bg-gray-900 text-white font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-lg active:scale-95"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
